import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, Observable, Subscription } from 'rxjs';
import { TimerDigitPipe } from '../../../../pipes/timer.pipe';
import { TimerService } from '../../../../services/timer.service';

interface Counter {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

@Component({
  selector: 'app-timer',
  imports: [TimerDigitPipe, NgClass],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./timer.component.scss'],
  templateUrl: './timer.component.html',
})
export class AppTimerBaseComponent implements OnInit {
  /**
   * mode = global -> The component will be showed in all the store pages. If there is more than 1 timers marked as global
   * at the same time we select the last timer added with a dateTime > currentDateTime
   *
   * mode = specific -> The timer showed will be the one getting by the timerId if exists and if dateTime is > currentDateTime
   * */
  @Input({ required: true }) mode!: string;
  /**s
   * This input is required if the mode is != global
   * */
  @Input() timerId?: string;

  private timerService = inject(TimerService);
  private cdk = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);
  private endDateTime?: Date;
  private dateSeed$?: Observable<any>;
  private counterSub$?: Subscription;

  public textTimer: string = '';
  public counter?: Counter;
  public loadData: boolean = false;

  ngOnInit(): void {
    if (this.mode == 'global') {
      this.dateSeed$ = this.timerService.getGlobalTimer();
    }

    if (this.mode == 'specific' && this.timerId != null) {
      this.dateSeed$ = this.timerService.getTimerById(this.timerId);
    }

    if (this.dateSeed$ !== null) {
      this.dateSeed$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((item) => {
        this.endDateTime = new Date(item.timerDate);
        this.textTimer = item.text;
        if (this.isGlobal(item) || this.isSpecific(item)) {
          this.loadData = true;
          this.cdk.markForCheck();
          this.startTimer();
        }
      });
    }
  }

  private startTimer(): void {
    this.zone.runOutsideAngular(() => {
      this.counterSub$ = interval(1000)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.updateTimer();
        });
    });
  }

  private updateTimer(): void {
    const startDateTime = new Date();
    if (this.endDateTime == null) {
      this.counterSub$?.unsubscribe();
      return this.updateCounter({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    }

    const diffInMs = (this.endDateTime?.getTime() ?? 0) - startDateTime.getTime();

    if (diffInMs <= 0) {
      this.counterSub$?.unsubscribe();
      return this.updateCounter({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    }

    const newCounter = {
      days: Math.floor(diffInMs / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diffInMs / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diffInMs / (1000 * 60)) % 60),
      seconds: Math.floor((diffInMs / 1000) % 60),
    };
    return this.updateCounter(newCounter);
  }

  private updateCounter(data: Counter): void {
    this.counter = data;
    this.cdk.markForCheck();
  }

  private isGlobal(data: any): boolean {
    return this.mode == 'global' && data.allPages;
  }

  private isSpecific(data: any): boolean {
    return this.mode == 'specific' && !data.allPages;
  }
}
